import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import { useAuthStore } from '@/stores/authStore';
import { ApiClientError, type ApiError } from '@/types/api';

export const http = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    Accept: 'application/json',
  },
});

http.interceptors.request.use((config) => {
  const { accessToken, deviceId } = useAuthStore.getState();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  config.headers['X-Device-Id'] = deviceId;

  return config;
});

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, deviceId, setTokens, clearSession } = useAuthStore.getState();

  if (!refreshToken) {
    clearSession();
    return null;
  }

  try {
    const response = await axios.post<{ data: { access_token: string; refresh_token: string } }>(
      `${env.apiBaseUrl}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'X-Device-Id': deviceId } },
    );

    const { access_token, refresh_token } = response.data.data;
    setTokens(access_token, refresh_token);

    return access_token;
  } catch {
    clearSession();
    return null;
  }
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const isAuthEndpoint = config?.url?.startsWith('/auth/');

    if (status === 401 && config && !config._retried && !isAuthEndpoint) {
      config._retried = true;

      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });

      const newToken = await refreshPromise;

      if (newToken) {
        config.headers.Authorization = `Bearer ${newToken}`;
        return http(config);
      }

      window.location.assign('/login');
    }

    const payload = error.response?.data;
    throw new ApiClientError(
      payload?.message ?? error.message ?? 'An unexpected error occurred.',
      payload?.error?.code ?? 'NETWORK_ERROR',
      status ?? 0,
      payload?.error?.details ?? null,
    );
  },
);
